import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GroupClassroomContent as Content } from "content";
import { useAddCourseToGroup, useCourses } from "hooks/api/courses";
import { Empty } from "components/Common/Empty";
import { Space } from "components/Common/Space";
import { Loader } from "components/Common/Loader";
import { ContentModal } from "components/Common/ContentModal";
import { MaterialsCoverItem } from "components/Courses/MaterialsCoverItem";
import { MaterialsPagination } from "components/Courses/MaterialsPagination";
import { Route } from "constants/routes";
import { TAddCourseToGroupBody, TCourse } from "types/api/course";
import "./AddGroupMaterialModal.styles.scss";

type TAddGroupMaterialModalProps = {
  /**
   * If `true`, the modal is shown.
   *
   * @default false
   */
  open?: boolean;
  /**
   * List of courses that the group is already enrolled in.
   * This is used to filter out the courses that the group is already enrolled in.
   */
  existingCourses?: TCourse[];
  /**
   * Callback fired when the component requests to be closed.
   */
  onClose: () => void;
};

export const AddGroupMaterialModal: React.FC<TAddGroupMaterialModalProps> = (
  props
) => {
  const { open = false, existingCourses = [], onClose } = props;

  const { groupId } = useParams();
  const navigate = useNavigate();

  const [pageNumber, setPageNumber] = useState<number>(0);

  const { data: coursesData, isFetching: isCourseLoading } = useCourses({
    pageNumber,
    // For the modal, we want to show all courses, not group-specific ones
    // so we don't pass groupId here
  });

  const { mutate: addCourseToGroup } = useAddCourseToGroup();

  // TODO: check if this is correct filtering
  const courses = useMemo(
    () =>
      (coursesData?.content || []).filter((course) =>
        existingCourses.every(
          (existingCourse) => existingCourse.id !== course.id
        )
      ),
    [coursesData?.content, existingCourses]
  );
  const totalPages: number = useMemo(
    () => coursesData?.totalPages || 0,
    [coursesData?.totalPages]
  );
  const isEmpty: boolean = useMemo(() => courses.length === 0, [courses]);

  const modalCloseHandler = () => {
    onClose?.();
  };

  const pageChangeHandler = (newPage: number) => {
    setPageNumber(newPage);
  };

  const courseClickHandler = (courseId: number) => {
    if (!groupId) return;

    const body: TAddCourseToGroupBody = {
      groupId: parseInt(groupId),
    };

    addCourseToGroup(
      {
        courseId,
        body,
      },
      {
        onSuccess: () => {
          onClose?.();
          navigate(
            Route.App.Study.GROUP_CLASSROOM_COURSE.construct(
              groupId,
              courseId.toString()
            )
          );
        },
      }
    );
  };

  return (
    <ContentModal
      portalId="add-group-material-modal"
      open={open}
      title={Content.Add.TITLE}
      emoji={Content.Add.EMOJI}
      onClose={modalCloseHandler}
      className="nb-sub-group-classroom-add-course-modal"
    >
      <Space
        direction="vertical"
        justify="start"
        align="start"
        size="medium"
        fullWidth
      >
        {isCourseLoading ? (
          <Loader />
        ) : isEmpty && !isCourseLoading ? (
          <Empty text={Content.Add.Empty.MESSAGE} />
        ) : (
          <ul className="nb-sub-group-classroom-add-course-list">
            {courses.map((course) => (
              <MaterialsCoverItem
                key={course.id}
                course={course}
                onClick={courseClickHandler}
              />
            ))}
          </ul>
        )}
        <MaterialsPagination
          currentPage={pageNumber}
          totalPages={totalPages}
          onPageChange={pageChangeHandler}
        />
      </Space>
    </ContentModal>
  );
};
